import React, { useMemo, useState } from 'react';
import Geosuggest from 'react-geosuggest';
import styled, { css } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';
import { GOOGLE_MAPS_CLIENT_API_KEY } from '../../../../../config/index';
import DesktopSeparateFields from './DesktopSeparateFields';
import { safeGet } from '../../../../../core/util/object';
import {
  ADDRESS_FIELD_NAME,
  CITY_FIELD_NAME,
  STATE_CODE_FIELD_NAME,
  ZIP_CODE_FIELD_NAME,
  ADDRESS_SECONDARY_FIELD_NAME,
} from '../../../util/validators';
import { colors } from '../../../../../constants/colors';
import SectionTitle from '../../../Desktop/components/Sections/SectionTitle';
import { isBookingWidgetAddressRequired } from '../../../../../core/util/location';
import {
  shouldShowFeature,
  BOOKING_WIDGET_GEOSUGGEST,
} from '../../../../../core/util/customization';
import { DESKTOP, MOBILE } from '../../../util';
import MobileSeparateFields from './MobileSeparateFields';
import { analyticsTrackEvent } from '../../../../../core/analytics';
import {
  BOOKING_WIDGET_TOUCH_GEOSUGGEST,
  BOOKING_WIDGET_SELECT_GEOSUGGEST,
} from '../../../../../core/analytics/events';
import { isClientSide } from '../../../../../core/util/system';
import { EN, SupportedLocale } from '../../../../../core/util/localizedStr';

const googlePlacesUrl = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_CLIENT_API_KEY}&libraries=places`;

const BookingWidgetHomeAddressContainer = styled.div.withConfig({ componentId: 'sc-6egzo6-0' })`
  & .placeholderAsLabel {
    padding-bottom: 0px !important;
  }
`;
const StyledGeosuggest = styled(Geosuggest).withConfig({ componentId: 'sc-6egzo6-1' })`
  height: 58px;
  margin-top: 15px;

  .geosuggest__suggests--hidden {
    max-height: 0;
    overflow: hidden;
    border-width: 0;
  }

  .geosuggest__item--active {
    background: ${colors.greyHounds};
  }

  .geosuggest__input {
    height: 58px;
    padding: 0;
    box-sizing: border-box;
    font-size: 16px;
    border: 2px solid ${colors.greyHounds};
    width: 100%;
    padding: 20px;
    border-radius: 5px;
    color: ${colors.black};
    -webkit-appearance: none; /* https://stackoverflow.com/a/10757425 */

    ::placeholder {
      font-size: 18px;
      color: ${colors.lanaDelGrey};
    }

    ::-moz-placeholder {
      /* Firefox 19+ */
      opacity: 1;
    }

    &:focus {
      outline-color: ${colors.greyHounds};
    }

    ${(props) =>
      // @ts-expect-error ts-migrate(2339) FIXME: Property 'version' does not exist on type 'ThemedS... Remove this comment to see the full error message
      props.version === MOBILE &&
      css`
        border: 1px solid ${colors.greyHounds};
        border-right: none;
        border-left: none;
        border-radius: 0;
        font-size: 20px;

        ::-webkit-input-placeholder {
          /* Chrome/Opera/Safari */
          padding-top: 4px;
        }
      `}
  }

  .geosuggest__suggests-wrapper {
    display: flex;
    justify-content: center;
  }

  .geosuggest__suggests {
    position: relative;
    z-index: 5;
    background: ${colors.theWhiteAlbum};
    margin: 0;
    padding: 0;
    list-style: none;
    box-shadow: 0 3px 3px 0 rgba(0, 0, 0, 0.12), 0 8px 8px 0 rgba(0, 0, 0, 0.1);
    top: -3px;
    width: 99.5%;

    /* inspired by https://github.com/ubilabs/react-geosuggest/issues/64#issuecomment-308886827 */
    &:after {
      content: '';
      display: block;
      background-image: url('/images/homepage/powered-by-google.png');
      background-position: 98% 50%;
      background-repeat: no-repeat;
      background-size: 21%;
      height: 19px;
      border-top: 1px solid ${colors.borderLine};
    }
  }

  .geosuggest__item {
    font-size: 16px;
    cursor: pointer;
    color: ${colors.myRightFoot};
    border-top: 1px solid ${colors.borderLine};
    padding: 10px 22px;

    :hover {
      background-color: ${colors.greyHounds};
    }
  }

  .geosuggest__item__matched-text {
    color: ${colors.crunchBerry};
  }
`;

const suggestTypesToShowInDropdown = ['premise', 'street_address'];

const getValueForFieldType = (type: any, addressComponents: any, format = 'long_name') => {
  if (!addressComponents) return '';

  const component = addressComponents.find((c: any) => c.types[0] === type);
  return component ? component[format] : '';
};

const buildFieldValues = (addressComponents: any) => {
  const addressFieldValue = [
    getValueForFieldType('street_number', addressComponents),
    getValueForFieldType('route', addressComponents),
  ]
    .filter(Boolean)
    .join(' ');

  return {
    [ADDRESS_FIELD_NAME]: addressFieldValue,
    [ADDRESS_SECONDARY_FIELD_NAME]: getValueForFieldType('subpremise', addressComponents),
    [CITY_FIELD_NAME]: getValueForFieldType('locality', addressComponents),
    [STATE_CODE_FIELD_NAME]: getValueForFieldType(
      'administrative_area_level_1',
      addressComponents,
      'short_name'
    ),
    [ZIP_CODE_FIELD_NAME]: getValueForFieldType('postal_code', addressComponents),
  };
};

interface Props {
  form: any;
  initialValues: any;
  isUberRequest?: boolean;
  location: any;
  sectionTitle?: string;
  setZIndexGivenError: (...args: any[]) => any;
  submitted: boolean;
  validateField: (...args: any[]) => any;
  version?: typeof DESKTOP | typeof MOBILE;
  placeholder?: string;
  locale?: SupportedLocale;
}

const HomeAddress = (props: Props) => {
  const {
    form,
    initialValues,
    location,
    setZIndexGivenError,
    submitted,
    validateField,
    isUberRequest = false,
    version = DESKTOP,
    locale = EN,
  } = props;

  const { t } = useTranslation('bookingWidget');

  const sectionTitle = props.sectionTitle ?? `${t('userProfileAndPatientInfo.homeAddress')}:`;

  const placeholder =
    props.placeholder ??
    `${t('userProfileAndPatientInfo.homeAddressSeparateFields.streetAddress')}:`;

  const [isGoogleApiLoaded, setIsGoogleApiLoaded] = useState(isClientSide() && window.google);

  const handleScriptInject = (newState: any, addedTags: any) => {
    const { scriptTags } = addedTags;

    if (scriptTags) {
      const googleApiScript = scriptTags.find(({ src }: any) => src === googlePlacesUrl);
      if (googleApiScript) {
        // @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'true' is not assignable to param... Remove this comment to see the full error message
        googleApiScript.addEventListener('load', () => setIsGoogleApiLoaded(true));
      }
    }
  };

  const trackUsage = (eventName: any) => {
    analyticsTrackEvent(eventName, { locationId: location.id, version });
  };

  const onSuggestSelect = (suggest: any) => {
    // For some reason this function get called on onChange (particularly
    // when the input is empty). No need to run this function in that case.
    if (suggest === undefined) return;

    trackUsage(BOOKING_WIDGET_SELECT_GEOSUGGEST);

    const addressComponents = safeGet(suggest)('gmaps.address_components');
    const fieldValues = buildFieldValues(addressComponents);
    for (const fieldName in fieldValues) {
      if (fieldValues.hasOwnProperty(fieldName)) {
        // @ts-expect-error ts-migrate(7053) FIXME: No index signature with a parameter of type 'strin... Remove this comment to see the full error message
        form.setFieldsValue({ [fieldName]: fieldValues[fieldName] });
      }
    }
  };

  /* Not all suggests will come with/populate zip code info when Mary clicks it (which we require).
     So ideally we'd be able to skip the suggests that we know won't come with zip code info so
     they don't appear in the dropdown, but Google doesn't provide any zip code info until it's
     clicked on (and it makes another request). The suggest argument object in skipSuggest follows
     the google.maps.places.AutocompletePrediction interface which is limited.

     So the next best thing is that it appears (just from manual trial and error) that suggests
     with certain types usually come with zip code info. So we can skip any suggests that
     are not of these certain types. We can add more types to this list if we want more
     addresses to appear in the dropdown, we'll just want to be careful they'll also come with
     zip code info.

     See https://developers.google.com/maps/documentation/geocoding/overview#Types for more info */
  const skipSuggest = (suggest: any) =>
    !suggestTypesToShowInDropdown.some((type) => suggest.types.includes(type));

  const isGeosuggestEnabled =
    isGoogleApiLoaded && shouldShowFeature(BOOKING_WIDGET_GEOSUGGEST, location);

  if (!isBookingWidgetAddressRequired(location) && !isUberRequest) return null;

  return (
    <BookingWidgetHomeAddressContainer>
      <Helmet onChangeClientState={handleScriptInject}>
        <script defer src={googlePlacesUrl} />
      </Helmet>

      {version === DESKTOP && <SectionTitle title={sectionTitle} />}
      {isGeosuggestEnabled && (
        <StyledGeosuggest
          autoComplete="new-password" // to prevent the browser's default auto-fill popup. see https://stackoverflow.com/a/38961567
          country="us"
          onFocus={() => trackUsage(BOOKING_WIDGET_TOUCH_GEOSUGGEST)}
          onSuggestSelect={onSuggestSelect}
          placeholder={
            locale === EN && version === MOBILE
              ? 'Enter your home address here'
              : t('userProfileAndPatientInfo.enterYourAddressHere')
          }
          skipSuggest={skipSuggest}
          types={['address']}
          // @ts-expect-error ts-migrate(2769) FIXME: Property 'version' does not exist on type 'Intrins... Remove this comment to see the full error message
          version={version}
        />
      )}
      {version === DESKTOP ? (
        <DesktopSeparateFields
          form={form}
          initialValues={initialValues}
          location={location}
          setZIndexGivenError={setZIndexGivenError}
          submitted={submitted}
          validateField={validateField}
        />
      ) : (
        <MobileSeparateFields
          form={form}
          initialValues={initialValues}
          location={location}
          placeholder={placeholder}
          setZIndexGivenError={setZIndexGivenError}
          submitted={submitted}
          validateField={validateField}
        />
      )}
    </BookingWidgetHomeAddressContainer>
  );
};

export default HomeAddress;
